import { getNextTransition } from '@wix/ambassador-funnel-builder-v2-flow-context/http'
import { getPageContent } from '@wix/ambassador-funnel-builder-v2-page/http'
import {
  FlowContext,
  FunnelBuilderService,
  GetPageContentParams,
  Page,
  pageMapper,
  Transition,
  transitionMapper,
} from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'

import { FlowMonitoring } from '../../monitoring/FlowMonitoring'
import { withMonitoring } from '../../monitoring/withMonitoring'
import { ambassadorHttpClient } from '../httpClient'

export class HttpFunnelBuilderService implements FunnelBuilderService {
  private monitoring: FlowMonitoring
  private onRequestError?: () => void

  constructor(flowId: string, onRequestError?: () => void) {
    this.monitoring = new FlowMonitoring(flowId)
    this.onRequestError = onRequestError
  }

  async getNextTransition(flowContext: FlowContext): Promise<Transition> {
    const request = getNextTransition({ flowContext })
    const [error, response] = await tryCatch(() =>
      withMonitoring(() => ambassadorHttpClient.request(request), 'getNextTransition'),
    )
    if (error) {
      this.onRequestError?.()
      throw error
    }
    const { data } = response

    const originPageId = flowContext.historyEntries[flowContext.historyEntries.length - 1]!.pageId!
    await this.monitoring.sendTransitionStarted({
      originId: originPageId,
      destinationId: data.transition!.internal?.targetPageId,
      isExternal: Boolean(data.transition?.external),
    })

    return transitionMapper.toDomain(data.transition!)
  }

  async getPageContent({
    flowContext,
    pageId: requestPageId,
    forceLanguage,
    debugTranslationKeys,
  }: GetPageContentParams): Promise<Page> {
    const historyEntries = flowContext?.historyEntries
    const snapshotId = historyEntries ? historyEntries[historyEntries.length - 1]?.snapshotId : undefined
    const pageId = requestPageId ? requestPageId : historyEntries![historyEntries!.length - 1]!.pageId!

    const request = getPageContent({ pageId, flowContext, snapshotId, forceLanguage, debugTranslationKeys })
    const [error, response] = await tryCatch(() =>
      withMonitoring(() => ambassadorHttpClient.request(request), 'getPageContent'),
    )
    if (error) {
      this.onRequestError?.()
      throw error
    }
    const { data } = response
    await Promise.all([
      this.monitoring.sendPageView({ pageId }),
      this.monitoring.sendTransitionFinished({ destinationId: pageId }),
    ])

    return pageMapper.toDomain(data.page!)
  }

  async initFlowContext(): Promise<FlowContext> {
    throw new Error('Not implemented')
  }
}
